import { useEffect, useState } from 'react';
import { createImageUrlBuilder } from '@sanity/image-url';

/**
 * Conexión de solo lectura con Sanity, donde el equipo carga portafolio, clientes
 * y blog (panel en studio/). El ID del proyecto no es secreto: va en cada URL de
 * imagen. Mientras no esté definido, todo el sitio usa los datos locales de
 * src/Data, así que funciona igual antes y después de crear el proyecto.
 */
export const SANITY = {
    projectId: process.env.REACT_APP_SANITY_PROJECT_ID || '',
    dataset: process.env.REACT_APP_SANITY_DATASET || 'production',
    apiVersion: '2025-10-01',
};

export const sanityConfigurado = Boolean(SANITY.projectId);

// El sitio abierto dentro de la vista previa del panel (un iframe del Studio)
export const enVistaPrevia = typeof window !== 'undefined' && window.self !== window.top;

const imagenes = sanityConfigurado ? createImageUrlBuilder({ projectId: SANITY.projectId, dataset: SANITY.dataset }) : null;

// Una promesa por consulta: navegar entre páginas no repite la descarga
const cache = new Map();

/** Consulta GROQ al CDN de Sanity (datos publicados, sin token). */
export function consultar(query, params = {}) {
    const url = new URL(`https://${SANITY.projectId}.apicdn.sanity.io/v${SANITY.apiVersion}/data/query/${SANITY.dataset}`);
    url.searchParams.set('query', query);
    Object.entries(params).forEach(([clave, valor]) => url.searchParams.set(`$${clave}`, JSON.stringify(valor)));
    const clave = url.toString();

    if (!cache.has(clave)) {
        const pedido = fetch(clave)
            .then((r) => {
                if (!r.ok) throw new Error(`Sanity respondió ${r.status}`);
                return r.json();
            })
            .then((json) => json.result)
            .catch((error) => {
                cache.delete(clave);
                throw error;
            });
        cache.set(clave, pedido);
    }
    return cache.get(clave);
}

/**
 * Datos de una consulta con respaldo local: sin Sanity configurado, o si la
 * consulta falla, devuelve `local` (ya transformado al mismo formato).
 */
export function useConsulta(query, params, local) {
    const [estado, setEstado] = useState(() => ({
        datos: sanityConfigurado ? null : local,
        cargando: sanityConfigurado,
        error: null,
    }));
    const claveParams = JSON.stringify(params || {});

    useEffect(() => {
        if (!sanityConfigurado) return undefined;
        let vigente = true;

        // Dentro del panel, los datos (con borradores) llegan del Studio y se
        // actualizan mientras el equipo escribe (ver presentacion.js)
        if (enVistaPrevia) {
            let desuscribir = () => {};
            import('./presentacion').then(({ suscribir }) => {
                if (!vigente) return;
                desuscribir = suscribir(query, JSON.parse(claveParams), ({ data, loading, error }) => {
                    if (error) setEstado({ datos: local, cargando: false, error });
                    else if (!loading || data !== undefined) setEstado({ datos: data ?? null, cargando: false, error: null });
                });
            });
            return () => {
                vigente = false;
                desuscribir();
            };
        }

        consultar(query, JSON.parse(claveParams))
            .then((datos) => { if (vigente) setEstado({ datos, cargando: false, error: null }); })
            .catch((error) => { if (vigente) setEstado({ datos: local, cargando: false, error }); });
        return () => { vigente = false; };
        // `local` es una constante del módulo que llama: no cambia entre renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, claveParams]);

    return estado;
}

/**
 * URL de una imagen: las de Sanity llegan en WebP o AVIF y al ancho pedido; una
 * imagen local (string) se devuelve tal cual.
 */
export function urlImagen(imagen, { ancho, alto, calidad = 80 } = {}) {
    if (!imagen) return '';
    if (typeof imagen === 'string') return imagen;
    if (!imagenes || !imagen.asset) return '';
    let url = imagenes.image(imagen).auto('format').quality(calidad);
    if (ancho) url = url.width(ancho);
    if (alto) url = url.height(alto).fit('crop');
    return url.url();
}

/** Anchos para `srcSet`: el navegador elige según el tamaño en pantalla y la densidad. */
export function srcSetImagen(imagen, anchos = [480, 800, 1200, 1600, 2400]) {
    if (!imagen || typeof imagen === 'string' || !imagenes) return undefined;
    return anchos.map((ancho) => `${urlImagen(imagen, { ancho })} ${ancho}w`).join(', ');
}

/** Punto de interés que el equipo marca en Sanity, como `object-position` para `object-cover`. */
export function posicionImagen(imagen, porDefecto) {
    const punto = imagen && typeof imagen === 'object' ? imagen.hotspot : null;
    return punto ? `${Math.round(punto.x * 100)}% ${Math.round(punto.y * 100)}%` : porDefecto;
}
